// MCB Document Automation Agent — API client wrappers.
//
// Three things live here:
//   1. `apiFetch(path, opts)` — thin wrapper over `fetch` with our
//      same-origin convention, JSON content-type by default, and a
//      hook for the 401-on-session-expired flow.
//   2. `cachedFetch(path, ttlMs)` — opt-in in-memory TTL cache layer
//      over apiFetch. Used for read-heavy GETs like `/api/matters`
//      and `/api/templates` that re-mount frequently during nav.
//   3. `invalidateApiCache(pattern)` — bust the cache after a mutation
//      (e.g., `invalidateApiCache("/api/matters")` after creating a
//      new matter so the dashboard shows the new row immediately).
//
// `setSessionExpiredHandler` lets the App component register a
// callback that fires when any API call returns 401. Decouples the
// API layer from React state.

// Exported: MatterDetailPage and GenerateDocumentCard build raw fetch()
// URLs with it. MatterDetailPage referenced a bare `API` for a long time
// without declaring or importing one — it only worked because Rollup
// flattens ES modules into a single bundle scope, so this constant
// happened to be visible. Under any other bundler, or the moment the
// name collided and got renamed, those calls would have thrown
// ReferenceError.
export const API = window.location.origin;

// Set by App via setSessionExpiredHandler — apiFetch calls it on 401
// so the UI can show the "Clio sign-in required" modal.
let _onSessionExpired = null;

export function setSessionExpiredHandler(handler) {
  _onSessionExpired = handler;
}

// ────────────────────────────────────────────────────────────────────
// Per-path circuit breaker
// ────────────────────────────────────────────────────────────────────
// Defence in depth against request floods. On 2026-08-17 an unstable
// `showNotification` identity closed a render/fetch loop (see the note
// in app.jsx) and a single backend hiccup became ~22,000 requests in
// seconds — the browser hit ERR_INSUFFICIENT_RESOURCES and Railway
// started 502ing. The root cause is fixed, but a client should never be
// able to do that to the server, whatever new loop someone writes next.
//
// So: if one path fails FAILURE_LIMIT times inside FAILURE_WINDOW_MS,
// stop hitting the network for that path for COOLDOWN_MS and fail fast
// instead. Any success clears the record. A human retrying after the
// cooldown works normally; only a machine hammering gets stopped.
const FAILURE_LIMIT = 6;
const FAILURE_WINDOW_MS = 5_000;
const COOLDOWN_MS = 15_000;
const _failures = new Map(); // path -> { count, first, openUntil }

export function resetCircuitBreaker(path) {
  if (path === undefined) _failures.clear();
  else _failures.delete(path);
}

function _breakerBlocked(path) {
  const rec = _failures.get(path);
  if (!rec || !rec.openUntil) return false;
  if (Date.now() >= rec.openUntil) { _failures.delete(path); return false; }
  return true;
}

function _recordFailure(path) {
  const now = Date.now();
  const rec = _failures.get(path);
  if (!rec || now - rec.first > FAILURE_WINDOW_MS) {
    _failures.set(path, { count: 1, first: now, openUntil: 0 });
    return;
  }
  rec.count += 1;
  if (rec.count >= FAILURE_LIMIT) {
    rec.openUntil = now + COOLDOWN_MS;
    console.warn(`[api] circuit open for ${path} — ${rec.count} failures in ${FAILURE_WINDOW_MS}ms, pausing ${COOLDOWN_MS}ms`);
  }
}

export async function apiFetch(path, opts = {}) {
  if (_breakerBlocked(path)) {
    const error = new Error("Too many failed attempts — paused for a few seconds. Reload to retry.");
    error.status = 0;
    error.circuitOpen = true;
    throw error;
  }
  // FormData sets its own Content-Type, including the multipart
  // boundary the server needs to parse the body. Forcing
  // application/json over it produces a request the server rejects as
  // malformed, so file uploads have to be let through untouched.
  const isMultipart = typeof FormData !== "undefined"
    && opts.body instanceof FormData;
  let res;
  try {
    res = await fetch(`${API}${path}`, {
      headers: isMultipart
        ? { ...opts.headers }
        : { "Content-Type": "application/json", ...opts.headers },
      ...opts,
    });
  } catch (e) {
    // Network-level failure (offline, connection pool exhausted, DNS).
    // These are exactly the ones a flood produces, so they must count.
    _recordFailure(path);
    throw e;
  }
  if (!res.ok) _recordFailure(path); else _failures.delete(path);
  if (res.status === 401 && _onSessionExpired) {
    _onSessionExpired();
    throw new Error("Session expired — please sign in again.");
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    const error = new Error(err.detail || err.message || `API error ${res.status}`);
    // Attach status + full response body so callers that need structured
    // access to error data (e.g. the 422 missing-fields response has a
    // `missing` array) can read `e.status` / `e.body`. Existing callers
    // that only use `e.message` are unaffected.
    error.status = res.status;
    error.body = err;
    throw error;
  }
  if (res.status === 204) return {};
  if (opts.raw) return res;
  return res.json();
}

// ────────────────────────────────────────────────────────────────────
// In-memory TTL cache for GET responses
// ────────────────────────────────────────────────────────────────────
// Backs `cachedFetch()` below. Keeps recently-fetched API responses in
// a Map keyed by path so that re-mounting components (sidebar nav,
// browser-back, opening a matter and returning) doesn't re-pay the
// Clio API latency for endpoints that don't change second-to-second.
//
// `cachedFetch` is opt-in — mutation calls (POST/PUT/DELETE) and
// truly-fresh reads (review queue, audit log) keep using bare
// `apiFetch` and skip the cache entirely. Use `invalidateApiCache(path)`
// after a mutation that would invalidate a cached read.
const _apiCache = new Map(); // path -> { ts, data, inflight }

/** Drop cached responses, so the next read goes to the network.
 *
 * Pass a prefix to clear one family of paths; omit it to clear all.
 * Needed wherever a user action is expected to change what a cached
 * endpoint returns — refreshing a page into a sixty-second-old copy of
 * the thing you just rebuilt looks like the button did nothing.
 */
export function clearApiCache(prefix) {
  if (!prefix) { _apiCache.clear(); return; }
  for (const key of [..._apiCache.keys()]) {
    if (key.startsWith(prefix)) _apiCache.delete(key);
  }
}

export async function cachedFetch(path, ttlMs = 60_000) {
  const now = Date.now();
  const hit = _apiCache.get(path);
  if (hit && hit.data !== undefined && (now - hit.ts) < ttlMs) {
    return hit.data;
  }
  // Dedupe concurrent fetches — if a fetch for this path is already
  // in flight, return the same promise instead of firing a duplicate.
  if (hit && hit.inflight) return hit.inflight;
  const inflight = apiFetch(path)
    .then(data => {
      _apiCache.set(path, { ts: Date.now(), data });
      return data;
    })
    .catch(e => {
      _apiCache.delete(path);
      throw e;
    });
  _apiCache.set(path, { ts: hit?.ts || 0, data: hit?.data, inflight });
  return inflight;
}

// ────────────────────────────────────────────────────────────────────
// Stage dashboard — the matter page's hottest endpoint
// ────────────────────────────────────────────────────────────────────
// StageTracker, TasksCard, StageDocumentsCard and StagePackageCard all
// need the matter's current stage, and each was firing its own GET on
// mount. Server-side every one of those does a live Clio read (Clio
// owns the stage), so opening a matter and clicking Documents meant
// five sequential Clio round trips before the page settled — which is
// what "the documents page takes a while to load" actually was.
//
// Short TTL deliberately: Clio is authoritative and someone may change
// the stage there. 20s collapses a page load into one call while still
// reflecting reality on the next visit. Any mutation that can move the
// stage or its task progress passes force:true.
const STAGE_DASHBOARD_TTL_MS = 20_000;

export function fetchStageDashboard(matterId, { force = false } = {}) {
  const path = `/api/matters/${matterId}/stage/dashboard`;
  if (force) invalidateApiCache(path);
  return cachedFetch(path, STAGE_DASHBOARD_TTL_MS);
}

// The matter's provider roster is read by MatterDetailPage on mount and
// again by StageDocumentsCard. It only changes when someone adds or
// removes a provider, and those paths pass force:true.
const MATTER_PROVIDERS_TTL_MS = 60_000;

export function fetchMatterProviders(matterId, { force = false } = {}) {
  const path = `/api/matters/${matterId}/providers`;
  if (force) invalidateApiCache(path);
  return cachedFetch(path, MATTER_PROVIDERS_TTL_MS);
}

export function invalidateApiCache(pattern) {
  // String → substring match. RegExp → test match. No arg → clear all.
  if (pattern === undefined) {
    _apiCache.clear();
    return;
  }
  for (const key of Array.from(_apiCache.keys())) {
    const match = pattern instanceof RegExp ? pattern.test(key) : key.includes(pattern);
    if (match) _apiCache.delete(key);
  }
}
